import type { Task } from "../tasks/types.js";
import { mergeRecord } from "./merge-record.js";
import {
  emptyGraph,
  SET_FIELDS,
  type EntityKind,
  type FieldConflict,
  type GraphMerge,
  type SyncableRecord,
  type SyncGraph,
} from "./types.js";

interface EntityMerge<T> {
  merged: T[];
  toApply: T[];
  toPush: T[];
  conflicts: FieldConflict[];
}

function mergeEntity<T extends SyncableRecord>(
  kind: EntityKind,
  local: T[],
  remote: T[],
): EntityMerge<T> {
  const localById = new Map(local.map((r) => [r.id, r] as const));
  const remoteById = new Map(remote.map((r) => [r.id, r] as const));
  const ids = new Set<string>([...localById.keys(), ...remoteById.keys()]);
  const setFields = SET_FIELDS[kind];

  const out: EntityMerge<T> = { merged: [], toApply: [], toPush: [], conflicts: [] };
  for (const id of ids) {
    const res = mergeRecord(kind, localById.get(id), remoteById.get(id), setFields);
    out.merged.push(res.merged);
    if (res.changedLocally) out.toApply.push(res.merged);
    if (res.changedRemotely) out.toPush.push(res.merged);
    out.conflicts.push(...res.conflicts);
  }
  // Sort the converged view by id so two devices reach byte-identical state
  // regardless of which side's rows were enumerated first.
  out.merged.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return out;
}

/**
 * Converge a local and a remote graph. Deterministic regardless of argument order
 * (so two devices reach byte-identical state). Returns the merged graph, the diff
 * to write locally (`toApplyLocally`, with dangling references resolved), the diff
 * to push to the server (`toPushRemotely`), and any shadowed concurrent edits.
 */
export function mergeGraph(local: SyncGraph, remote: SyncGraph): GraphMerge {
  const tasks = mergeEntity("task", local.tasks, remote.tasks);
  const rules = mergeEntity("rule", local.recurrenceRules, remote.recurrenceRules);
  const overrides = mergeEntity("override", local.recurrenceOverrides, remote.recurrenceOverrides);
  const labels = mergeEntity("label", local.labels, remote.labels);
  const topics = mergeEntity("topic", local.topics, remote.topics);
  const calendars = mergeEntity("calendar", local.calendars, remote.calendars);
  const themes = mergeEntity("theme", local.themes, remote.themes);
  const preferences = mergeEntity("preferences", local.preferences, remote.preferences);

  const merged: SyncGraph = {
    tasks: tasks.merged,
    recurrenceRules: rules.merged,
    recurrenceOverrides: overrides.merged,
    labels: labels.merged,
    topics: topics.merged,
    calendars: calendars.merged,
    themes: themes.merged,
    preferences: preferences.merged,
  };

  const ruleIds = new Set(merged.recurrenceRules.map((r) => r.id));
  const toApplyLocally: SyncGraph = {
    ...emptyGraph(),
    recurrenceRules: rules.toApply,
    labels: labels.toApply,
    topics: topics.toApply,
    calendars: calendars.toApply,
    themes: themes.toApply,
    preferences: preferences.toApply,
    tasks: tasks.toApply.map((t) => degradeOrphanTask(t, ruleIds)),
    recurrenceOverrides: overrides.toApply.filter((o) => ruleIds.has(o.recurrence_id)),
  };

  const toPushRemotely: SyncGraph = {
    tasks: tasks.toPush,
    recurrenceRules: rules.toPush,
    recurrenceOverrides: overrides.toPush,
    labels: labels.toPush,
    topics: topics.toPush,
    calendars: calendars.toPush,
    themes: themes.toPush,
    preferences: preferences.toPush,
  };

  const conflicts = [
    ...tasks.conflicts,
    ...rules.conflicts,
    ...overrides.conflicts,
    ...labels.conflicts,
    ...topics.conflicts,
    ...calendars.conflicts,
    ...themes.conflicts,
    ...preferences.conflicts,
  ];

  return { merged, conflicts, toApplyLocally, toPushRemotely };
}

function degradeOrphanTask(t: Task, ruleIds: Set<string>): Task {
  if (t.recurrence_id && !ruleIds.has(t.recurrence_id)) {
    return { ...t, recurrence_id: null };
  }
  return t;
}
